import React from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  apptDetailFields,
  enrichAppointmentForDisplay,
} from '../../utils/agendaAppointmentDisplay';

type Props = {
  apt: any;
  formatDate: (d: string) => string;
  statusChip: React.ReactNode;
  patient?: { full_name?: string | null; birth_date?: string | null } | null;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  opening?: boolean;
  variant?: 'upcoming' | 'recent';
};

/**
 * Cartão de consulta no perfil do paciente — mesmos campos da grade da agenda
 * (tipo, idade, convênio/particular, profissional, observações).
 */
const PatientProfileAppointmentRow: React.FC<Props> = ({
  apt,
  formatDate,
  statusChip,
  patient,
  onClick,
  onKeyDown,
  opening,
  variant = 'upcoming',
}) => {
  const enriched = enrichAppointmentForDisplay(apt, patient);
  const f = apptDetailFields(enriched, patient?.full_name || 'Consulta');
  const meta = [f.typeLabel, f.age, f.billing].filter((p) => p && String(p).trim()).join(' · ');
  const timeLabel =
    f.timeStart && f.timeEnd && f.timeStart !== f.timeEnd
      ? `${f.timeStart} – ${f.timeEnd}`
      : f.timeStart;

  const isUpcoming = variant === 'upcoming';

  return (
    <Tooltip title="Clique para editar a consulta">
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Editar consulta de ${formatDate(apt.appointment_date)}`}
        onClick={onClick}
        onKeyDown={onKeyDown}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 0.5,
          py: 1.25,
          px: 1.5,
          borderRadius: 2,
          bgcolor: isUpcoming ? '#f8fafc' : '#fff',
          border: isUpcoming ? '1px solid rgba(59, 130, 246,0.12)' : '1px solid #f1f5f9',
          cursor: opening ? 'wait' : 'pointer',
          opacity: opening ? 0.75 : 1,
          transition: 'background-color 120ms ease, border-color 120ms ease',
          '&:hover': {
            bgcolor: isUpcoming ? '#eff6ff' : '#f8fafc',
            borderColor: isUpcoming ? 'rgba(59, 130, 246, 0.35)' : 'rgba(100, 116, 139, 0.35)',
          },
          '&:focus-visible': { outline: '2px solid #3b82f6', outlineOffset: 2 },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {opening && <CircularProgress size={14} sx={{ color: isUpcoming ? '#3b82f6' : '#64748b' }} />}
          {!isUpcoming && (
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8', flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            {formatDate(apt.appointment_date)}
          </Typography>
          {timeLabel && (
            <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.8rem' }}>
              {timeLabel}
            </Typography>
          )}
          <Box sx={{ ml: 'auto' }}>{statusChip}</Box>
        </Box>

        {meta && (
          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.78rem', lineHeight: 1.35 }}>
            {meta}
          </Typography>
        )}

        {f.prof && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            Dr(a). {f.prof}
          </Typography>
        )}

        {f.notes && (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.76rem',
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {f.notes}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default PatientProfileAppointmentRow;
